import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { BarChart3 } from 'lucide-react';
import { formatNrr } from '@/lib/cricket';
import { StandingsLegend } from './StandingsLegend';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function sortStandings(rows) {
  // Server now stamps an authoritative `rank` per pool computed as
  //   PTS → Head-to-Head wins → H2H point diff → Overall point diff → Name.
  // Fall back to legacy client-side sort only when `rank` is missing
  // (e.g., older cached payloads).
  return rows.slice().sort((a, b) => {
    const ar = a.rank, br = b.rank;
    if (ar != null && br != null) return ar - br;
    if ((b.total_points || 0) !== (a.total_points || 0)) return (b.total_points || 0) - (a.total_points || 0);
    if ((b.h2h_wins || 0) !== (a.h2h_wins || 0)) return (b.h2h_wins || 0) - (a.h2h_wins || 0);
    return (b.point_diff || 0) - (a.point_diff || 0);
  });
}

function StandingsTableCard({ title, rows, teams = [], teamLogos = {}, shareToken, labels = {}, headStyle }) {
  const teamById = {};
  (teams || []).forEach(t => { teamById[t.id] = t; });
  const pfShort = labels.points_for || 'PF';
  const paShort = labels.points_against || 'PA';
  const pdShort = labels.point_diff || 'Diff';
  const showNrr = rows.some((r) => 'net_run_rate' in r);
  return (
    <Card className="overflow-hidden border-slate-200">
      {title && (
        <div className="px-4 py-2" style={headStyle || { backgroundColor: '#00247D', color: '#FFFFFF' }} data-testid="public-standings-pool-head">
          <h3 className="font-bold uppercase tracking-wide text-sm" style={{ fontFamily: 'Barlow Condensed' }}>
            {title}
          </h3>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase w-10">#</th>
              <th className="px-3 py-2 text-left text-[11px] font-bold text-slate-500 uppercase">Team</th>
              <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase">GP</th>
              <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase">W</th>
              <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase">L</th>
              <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase">D</th>
              <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase" data-testid="public-col-pf">{pfShort}</th>
              <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase" data-testid="public-col-pa">{paShort}</th>
              <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase" data-testid="public-col-diff">{pdShort}</th>
              {showNrr && <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase" data-testid="public-col-nrr" title="Net Run Rate">NRR</th>}
              <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase">Pts</th>
              <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase">Win %</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const diff = row.point_diff ?? ((row.points_for || 0) - (row.points_against || 0));
              const logo = row.team_id ? (teamLogos[row.team_id] || teamById[row.team_id]?.logo) : null;
              return (
                <tr key={`${row.team_id || idx}`} className={`border-b border-slate-100 ${idx < 3 ? 'bg-amber-50/50' : ''}`} data-testid={`public-standings-row-${idx}`}>
                  <td className="px-3 py-2 text-center font-bold text-slate-500">{idx + 1}</td>
                  <td className="px-3 py-2 font-semibold text-slate-800">
                    {shareToken && row.team_id ? (
                      <Link to={`/share/${shareToken}/team/${row.team_id}`}
                        className="inline-flex items-center gap-2 hover:text-amber-600 hover:underline underline-offset-2"
                        data-testid={`standings-team-link-${row.team_id}`}>
                        {logo && (
                          <img src={`${API_URL}/api/files/${logo}`} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" onError={e => { e.target.style.display = 'none'; }} />
                        )}
                        {row.team_name}
                      </Link>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        {logo && (
                          <img src={`${API_URL}/api/files/${logo}`} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" onError={e => { e.target.style.display = 'none'; }} />
                        )}
                        {row.team_name}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center font-mono text-slate-600">{row.played || 0}</td>
                  <td className="px-3 py-2 text-center font-mono text-emerald-700 font-bold">{row.wins || 0}</td>
                  <td className="px-3 py-2 text-center font-mono text-rose-500">{row.losses || 0}</td>
                  <td className="px-3 py-2 text-center font-mono text-slate-500">{row.draws || 0}</td>
                  <td className="px-3 py-2 text-center font-mono text-slate-600">{row.points_for || 0}</td>
                  <td className="px-3 py-2 text-center font-mono text-slate-600">{row.points_against || 0}</td>
                  <td className={`px-3 py-2 text-center font-mono ${diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-rose-500' : 'text-slate-500'}`}>
                    {diff > 0 ? `+${diff}` : diff}
                  </td>
                  {showNrr && (
                    <td className={`px-3 py-2 text-center font-mono ${row.net_run_rate > 0 ? 'text-emerald-600' : row.net_run_rate < 0 ? 'text-rose-500' : 'text-slate-500'}`}
                      data-testid={`public-nrr-${row.team_id}`}>
                      {formatNrr(row.net_run_rate)}
                    </td>
                  )}
                  <td className="px-3 py-2 text-center font-bold text-[#00247D]">{row.total_points || 0}</td>
                  <td className="px-3 py-2 text-center text-slate-500 font-mono">{(row.win_percentage || 0).toFixed(0)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function StandingsTable({ standings, teams = [], teamLogos = {}, shareToken, labels = {}, tieBreakers = [], headStyle }) {
  // Normalize: standings is a dict keyed by team_id. Stamp the team_id onto each
  // row so the table can build a link to /share/{shareToken}/team/{team_id}.
  const rows = standings && typeof standings === 'object'
    ? Object.entries(standings).map(([tid, r]) => ({ team_id: tid, ...r }))
    : [];
  if (rows.length === 0) {
    return (
      <Card className="border-dashed border-slate-300 bg-slate-50" data-testid="public-standings-empty">
        <div className="py-12 text-center">
          <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-semibold mb-1">No standings available yet</p>
          <p className="text-slate-500 text-sm">Standings will appear here once match scores are entered.</p>
        </div>
      </Card>
    );
  }

  // Group by pool_name (null → single table)
  const byPool = {};
  rows.forEach(r => {
    const key = r.pool_name || '__all__';
    (byPool[key] = byPool[key] || []).push(r);
  });
  const poolKeys = Object.keys(byPool);
  const isMultiPool = !(poolKeys.length === 1 && poolKeys[0] === '__all__');

  return (
    <div className="space-y-6" data-testid="public-standings-view">
      {poolKeys.map(key => (
        <StandingsTableCard
          key={key}
          title={isMultiPool ? (key === '__all__' ? 'Unassigned' : key) : null}
          rows={sortStandings(byPool[key])}
          teams={teams}
          teamLogos={teamLogos}
          shareToken={shareToken}
          labels={labels}
          headStyle={headStyle}
        />
      ))}
      <StandingsLegend labels={labels} tieBreakers={tieBreakers} showNrr={rows.some((r) => 'net_run_rate' in r)} />
    </div>
  );
}
